import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartjsComponent } from '@coreui/angular-chartjs';
import { ChartData, ChartOptions } from 'chart.js';
import { ApiSlotsService } from '../../../../services/slots/api/api-slots.service';
import { AlertsService } from '../../../../services/alerts.service';
import { SlotCreditBalanceResultsResponse } from '../../../../models/slots/responses/api-slot-credit-balance-response.model';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_SECONDS = 86400;
const SEVEN_DAYS = 7;

@Component({
  selector: 'app-slot-statistics-tab',
  templateUrl: './slot-statistics-tab.component.html',
  standalone: true,
  imports: [CommonModule, ChartjsComponent]
})
export class SlotStatisticsTabComponent {

  isLoading: boolean = false;
  isLoaded: boolean = false;

  minGain: number = 0;
  maxGain: number = 0;
  averageGain: number = 0;
  minSpent: number = 0;
  maxSpent: number = 0;
  averageSpent: number = 0;
  averageBalance: number = 0;

  creditTimestamps: number[] = [];

  creditChartData: ChartData = { labels: [], datasets: [] };
  creditChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 60, bottom: 40 } },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          title: (items) => {
            const t = this.creditTimestamps[items[0].dataIndex];
            if (t === undefined) return '';
            const d = new Date(t * 1000);
            return `${DAY_LABELS[d.getUTCDay()]} ${d.getUTCDate().toString().padStart(2, '0')}/${(d.getUTCMonth() + 1).toString().padStart(2, '0')}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: (_value, index) => {
            const t = this.creditTimestamps[index];
            if (t === undefined) return '';
            return DAY_LABELS[new Date(t * 1000).getUTCDay()];
          }
        },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      y: { beginAtZero: true, ticks: { precision: 0 } }
    },
    elements: {
      line: { tension: 0.3 },
      point: { radius: 0, hitRadius: 10, hoverRadius: 4 }
    }
  };

  rakeChartData: ChartData = { labels: [], datasets: [] };
  rakeChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 60, bottom: 40 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const t = this.creditTimestamps[items[0].dataIndex];
            if (t === undefined) return '';
            const d = new Date(t * 1000);
            return `${DAY_LABELS[d.getUTCDay()]} ${d.getUTCDate().toString().padStart(2, '0')}/${(d.getUTCMonth() + 1).toString().padStart(2, '0')}`;
          },
          label: (item) => `Rake: ${(item.raw as number).toFixed(2)}%`
        }
      }
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: (_value, index) => {
            const t = this.creditTimestamps[index];
            if (t === undefined) return '';
            return DAY_LABELS[new Date(t * 1000).getUTCDay()];
          }
        },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => `${value}%` }
      }
    },
    elements: {
      line: { tension: 0.3 },
      point: { radius: 0, hitRadius: 10, hoverRadius: 4 }
    }
  };

  constructor(
    private apiSlotsService: ApiSlotsService,
    private alertsService: AlertsService
  ) {}

  load(): void {
    if (this.isLoaded || this.isLoading) return;
    this.refresh();
  }

  refresh(): void {
    this.isLoading = true;

    this.apiSlotsService.getSlotCreditBalance().then(
      (response: SlotCreditBalanceResultsResponse) => {
        this.isLoading = false;
        this.isLoaded = true;
        this.buildCreditChart(response);
      },
      error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading slot statistics : ' + error);
      }
    );
  }

  private buildCreditChart(response: SlotCreditBalanceResultsResponse): void {
    const byDay = new Map<number, { gain: number; spent: number; balance: number }>();
    for (const s of response.results) {
      const dayStart = Math.floor(s.dayTimestamp / DAY_SECONDS) * DAY_SECONDS;
      byDay.set(dayStart, { gain: s.gainCount, spent: s.spentCount, balance: s.balance });
    }

    const endDay = Math.floor(Date.now() / 1000 / DAY_SECONDS) * DAY_SECONDS;
    const startDay = endDay - (SEVEN_DAYS - 1) * DAY_SECONDS;

    const timestamps: number[] = [];
    const gains: number[] = [];
    const spents: number[] = [];
    const balances: number[] = [];
    for (let t = startDay; t <= endDay; t += DAY_SECONDS) {
      timestamps.push(t);
      const entry = byDay.get(t);
      gains.push(entry?.gain ?? 0);
      spents.push(entry?.spent ?? 0);
      balances.push(entry?.balance ?? 0);
    }

    this.creditTimestamps = timestamps;

    if (gains.length > 0) {
      this.minGain = Math.min(...gains);
      this.maxGain = Math.max(...gains);
      this.averageGain = Math.round(gains.reduce((sum, v) => sum + v, 0) / gains.length);
      this.minSpent = Math.min(...spents);
      this.maxSpent = Math.max(...spents);
      this.averageSpent = Math.round(spents.reduce((sum, v) => sum + v, 0) / spents.length);
      this.averageBalance = Math.round(balances.reduce((sum, v) => sum + v, 0) / balances.length);
    } else {
      this.minGain = 0;
      this.maxGain = 0;
      this.averageGain = 0;
      this.minSpent = 0;
      this.maxSpent = 0;
      this.averageSpent = 0;
      this.averageBalance = 0;
    }

    this.creditChartData = {
      labels: timestamps.map((_, i) => i.toString()),
      datasets: [
        {
          label: 'Gains',
          data: gains,
          backgroundColor: 'rgba(40, 167, 69, 0.15)',
          borderColor: 'rgba(40, 167, 69, 1)',
          pointHoverBackgroundColor: 'rgba(40, 167, 69, 1)',
          borderWidth: 2,
          fill: false
        },
        {
          label: 'Spent',
          data: spents,
          backgroundColor: 'rgba(220, 53, 69, 0.15)',
          borderColor: 'rgba(220, 53, 69, 1)',
          pointHoverBackgroundColor: 'rgba(220, 53, 69, 1)',
          borderWidth: 2,
          fill: false
        }
      ]
    };

    const rakes: number[] = spents.map((s, i) => s > 0 ? ((s - gains[i]) / s) * 100 : 0);

    this.rakeChartData = {
      labels: timestamps.map((_, i) => i.toString()),
      datasets: [{
        label: 'Rake (%)',
        data: rakes,
        backgroundColor: 'rgba(255, 159, 64, 0.15)',
        borderColor: 'rgba(255, 159, 64, 1)',
        pointHoverBackgroundColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 2,
        fill: true
      }]
    };
  }
}
